import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Star, Video } from "lucide-react";

export const AvaliacaoTrimestral = () => {
  const [pendente, setPendente] = useState(false);
  const [open, setOpen] = useState(false);
  const [estrelas, setEstrelas] = useState(0);
  const [comentario, setComentario] = useState("");
  const [autoriza, setAutoriza] = useState(false);
  const [video, setVideo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const location = useLocation();
  const rotaConfig = location.pathname.includes("/configuracoes") || location.pathname.includes("/perfil");

  useEffect(() => {
    if (sessionStorage.getItem("aval_trimestral_adiada")) return;
    (async () => {
      try {
        // Não abre por cima do modal de pendência fiscal (evita dois dialogs empilhados)
        const rb = await api.get("/montador/alertas-bloqueantes");
        if (rb.data?.bloqueia_acesso && rb.data?.alertas?.length &&
            !sessionStorage.getItem("nf_bloqueante_adiado")) return;
      } catch {}
      try {
        const r = await api.get("/montador/avaliacao-plataforma/status");
        if (r.data.pendente) { setPendente(true); setOpen(true); }
      } catch {}
    })();
  }, []);

  const adiar = () => {
    sessionStorage.setItem("aval_trimestral_adiada", "1");
    setOpen(false);
  };

  const enviar = async () => {
    if (!estrelas) { toast.error("Escolha uma nota de 1 a 5 estrelas"); return; }
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append("estrelas", estrelas);
      fd.append("comentario", comentario);
      fd.append("autoriza_uso_video", autoriza);
      if (video) fd.append("video", video);
      await api.post("/montador/avaliacao-plataforma", fd);
      toast.success("Obrigado pela sua avaliação! 🙌");
      setPendente(false);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEnviando(false); }
  };

  if (!pendente || rotaConfig) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) adiar(); else setOpen(o); }}>
      <DialogContent className="max-w-md" data-testid="avaliacao-trimestral-modal">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold">Avaliação trimestral · MontaJá</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            A cada 3 meses pedimos sua opinião para melhorar a plataforma. Leva menos de 1 minuto!
          </p>
          <div>
            <div className="label-cap mb-2">Como está sua experiência com a plataforma? *</div>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setEstrelas(n)}
                  data-testid={`aval-estrela-${n}`}
                  className={`p-1.5 rounded transition-transform hover:scale-110 ${n <= estrelas ? "text-[#F59E0B]" : "text-gray-300"}`}>
                  <Star size={28} fill={n <= estrelas ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="label-cap mb-2">Comentário (opcional)</div>
            <Textarea rows={3} value={comentario} onChange={(e) => setComentario(e.target.value)}
              placeholder="O que está bom? O que podemos melhorar?" data-testid="aval-comentario" />
          </div>
          <div>
            <div className="label-cap mb-2 flex items-center gap-1.5"><Video size={13} /> Vídeo depoimento de até 60s (opcional)</div>
            <input type="file" accept="video/*" onChange={(e) => setVideo(e.target.files?.[0] || null)}
              className="block text-sm" data-testid="aval-video" />
            {video && <div className="text-xs text-muted-foreground mt-1">{video.name}</div>}
            <label className="flex items-start gap-2 mt-3 cursor-pointer">
              <Checkbox checked={autoriza} onCheckedChange={(v) => setAutoriza(!!v)} data-testid="aval-autoriza" />
              <span className="text-xs text-muted-foreground">
                Autorizo o uso do meu vídeo/depoimento pela MontaJá em materiais de divulgação.
              </span>
            </label>
          </div>
          <div className="flex gap-2">
            <Button onClick={enviar} disabled={enviando} className="flex-1 btn-sharp brand-bg text-white" data-testid="aval-enviar">
              {enviando ? "Enviando…" : "Enviar avaliação"}
            </Button>
            <Button variant="outline" onClick={adiar} className="btn-sharp" data-testid="aval-depois">
              Depois
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground text-center">
            Este lembrete voltará a aparecer até você enviar a avaliação.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
